import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getListDocumentsQueryKey,
  useListDocuments,
} from "~/api/default/default";
import {
  Document,
  DocumentStatus,
  DocumentStatusCounts,
  ListDocuments200,
} from "~/api/model";
import { useGetCurrentUser } from "~/api/users/users";
import { fileMd5 } from "~/utils/fileMd5.ts";
import { useBatchKey } from "./useBatchKey";
import { useDocumentUpload } from "./useDocumentUpload";
import { useParentCase } from "./useParentCase.ts";

export type DocumentUpdateLog = {
  document: Document;
};
export type FileWithHash = File & { fileHash: string };
export type FileWithOptionalHash = File & { fileHash?: string };
export type DuplicateMatch = {
  newFileName: string;
  newFileSize: number;
  existingFileName: string;
};

export type IntraBatchDuplicate = {
  hash: string;
  files: { name: string; size: number }[];
};
const findDocumentStatusCounts = (
  documents: Document[] = [],
  status: DocumentStatus
) => {
  return documents.filter((doc) => doc.status === status).length || 0;
};

export const useDocumentsList = (organizationId: string, caseId: string) => {
  const { data, isLoading, isError } = useListDocuments(organizationId, caseId);
  const { data: currentUser } = useGetCurrentUser();
  const queryClient = useQueryClient();
  const [duplicateFiles, setDuplicateFiles] = useState<FileWithHash[]>([]);
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>(
    []
  );
  const [intraBatchDuplicates, setIntraBatchDuplicates] = useState<
    IntraBatchDuplicate[]
  >([]);
  const [pendingFiles, setPendingFiles] = useState<FileWithHash[]>([]);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);

  const { sessionBatchKey } = useBatchKey({
    userId: currentUser?.id || "",
    documents: data?.documents,
  });

  const { isNested } = useParentCase();

  const {
    uploadDocuments,
    cancelDraftUpload,
    uploadProgress,
    queuedDocuments,
    setQueuedDocuments,
    // setProcessingDocuments,
    // setUploadProgress,
    hasWaitingDocuments,
    pendingUploads,
    activeUploads,
  } = useDocumentUpload({
    organizationId,
    caseId,
    sessionBatchKey,
  });

  const bulkUpdateDocuments = (updates: DocumentUpdateLog[]) => {
    const key = getListDocumentsQueryKey(organizationId, caseId);
    let shouldInvalidate = false;

    queryClient.setQueryData<ListDocuments200 | undefined>(key, (old) => {
      if (!old || !old.documents) {
        shouldInvalidate = true;
        return old;
      }

      const byId = new Map(updates.map((u) => [u.document.id, u.document]));
      const nextDocs = old.documents.map((d) => byId.get(d.id) ?? d);

      for (const u of updates) {
        if (
          u.document.status === DocumentStatus.COMPLETE ||
          u.document.status === DocumentStatus.ERROR
        ) {
          shouldInvalidate = true;
        }
      }

      return { ...old, documents: nextDocs };
    });

    if (shouldInvalidate) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const isEmpty =
    (data?.documents?.length === 0 || data?.documents === undefined) &&
    queuedDocuments.length === 0;

  const hasOnlyArchivedDocuments = useMemo(() => {
    return isEmpty && isNested;
  }, [isEmpty, isNested]);

  // Clean up queuedDocuments when documents appear in API or are archived
  useEffect(() => {
    if (!data?.documents) return;

    const apiUploadIds = new Set(
      data.documents.map((doc) => doc.uploadId).filter(Boolean)
    );

    setQueuedDocuments((prev) => {
      return prev.filter((queuedDoc) => {
        return !apiUploadIds.has(queuedDoc.uploadId);
      });
    });
  }, [data?.documents, setQueuedDocuments]);

  const checkDuplicates = (
    files: FileWithHash[]
  ): {
    duplicates: FileWithHash[];
    matches: DuplicateMatch[];
    intraBatch: IntraBatchDuplicate[];
  } => {
    // 1. Inter-batch: files matching already-uploaded documents
    const existingDocs = data?.documents || [];
    const hashToFilename = new Map(
      existingDocs
        .filter(
          (doc): doc is typeof doc & { fileHash: string } => !!doc.fileHash
        )
        .map((doc) => [doc.fileHash, doc.filename])
    );
    const duplicates: FileWithHash[] = [];
    const matches: DuplicateMatch[] = [];
    for (const file of files) {
      const existingName = hashToFilename.get(file.fileHash);
      if (existingName) {
        duplicates.push(file);
        matches.push({
          newFileName: file.name,
          newFileSize: file.size,
          existingFileName: existingName,
        });
      }
    }

    // 2. Intra-batch: files within the upload set that share the same hash
    const nonInterDuplicateFiles = files.filter((f) => !duplicates.includes(f));
    const batchHashMap = new Map<string, { name: string; size: number }[]>();
    for (const file of nonInterDuplicateFiles) {
      const existing = batchHashMap.get(file.fileHash);
      const entry = { name: file.name, size: file.size };
      if (existing) {
        existing.push(entry);
      } else {
        batchHashMap.set(file.fileHash, [entry]);
      }
    }
    const intraBatch: IntraBatchDuplicate[] = [];
    for (const [hash, files] of batchHashMap) {
      if (files.length > 1) {
        intraBatch.push({ hash, files });
      }
    }

    return { duplicates, matches, intraBatch };
  };

  const handleUpload = async (files: FileWithHash[]): Promise<boolean> => {
    // Don't process uploads for nested versions
    if (isNested) {
      return false;
    }

    await queryClient.invalidateQueries({
      queryKey: getListDocumentsQueryKey(organizationId, caseId),
    });

    for (let i = 0; i < files.length; i += 1) {
      files[i].fileHash = await fileMd5(files[i]);
    }

    const { duplicates, matches, intraBatch } = checkDuplicates(files);

    if (duplicates.length > 0 || intraBatch.length > 0) {
      setDuplicateFiles(duplicates);
      setDuplicateMatches(matches);
      setIntraBatchDuplicates(intraBatch);
      setPendingFiles(files);
      setShowDuplicatesModal(true);
      return false;
    }
    const newQueuedDocs: (Document & { uploadId?: string })[] = files.map(
      (file) => ({
        filename: file.name,
        fileHash: file.fileHash,
        status: DocumentStatus.DRAFT,
        createdAt: new Date().toISOString(),
        authorId: currentUser!.id,
        author: currentUser!,
        folderId: caseId,
        previewUrl: "",
        size: file.size,
        type: file.type,
        contentType: file.type,
        downloadCount: 0,
        id: `queued-${file.name}-${Date.now()}`,
        s3Key: "",
        updatedAt: new Date().toISOString(),
        url: "",
        batchKey: sessionBatchKey,
        isStructured: false,
        structuredS3Key: "",
        uploadSize: file.size,
      })
    );

    setQueuedDocuments((prev) => {
      const filteredPrev = prev.filter(
        (doc) => !files.some((file) => file.name === doc.filename)
      );
      return [...filteredPrev, ...newQueuedDocs];
    });

    // await
    uploadDocuments(files as FileWithHash[]);
    return true;
  };

  const handleDuplicateConfirm = async () => {
    setShowDuplicatesModal(false);

    // Randomize fileHash for every file so the backend treats each as unique
    const filesWithUniqueHashes = pendingFiles.map(
      (file) =>
        Object.assign(new File([file], file.name, { type: file.type }), {
          fileHash: crypto.randomUUID(),
        }) as FileWithHash
    );

    const newQueuedDocs: (Document & { uploadId?: string })[] =
      filesWithUniqueHashes.map((file) => ({
        filename: file.name,
        fileHash: file.fileHash,
        status: DocumentStatus.DRAFT,
        createdAt: new Date().toISOString(),
        authorId: currentUser!.id,
        author: currentUser!,
        folderId: caseId,
        previewUrl: "",
        size: file.size,
        type: file.type,
        contentType: file.type,
        downloadCount: 0,
        id: `queued-${file.name}-${Date.now()}`,
        s3Key: "",
        updatedAt: new Date().toISOString(),
        url: "",
        batchKey: sessionBatchKey,
        isStructured: false,
        structuredS3Key: "",
        uploadSize: file.size,
      }));

    setQueuedDocuments((prev) => {
      const filteredPrev = prev.filter(
        (doc) =>
          !filesWithUniqueHashes.some((file) => file.name === doc.filename)
      );
      return [...filteredPrev, ...newQueuedDocs];
    });

    await uploadDocuments(filesWithUniqueHashes);
    setPendingFiles([]);
    setDuplicateFiles([]);
    setDuplicateMatches([]);
    setIntraBatchDuplicates([]);
  };

  const handleContinueWithoutDuplicates = async () => {
    setShowDuplicatesModal(false);

    // Remove inter-batch duplicates
    const interDupHashes = new Set(duplicateFiles.map((d) => d.fileHash));
    let nonDuplicates = pendingFiles.filter(
      (p) => !interDupHashes.has(p.fileHash)
    );

    // For intra-batch duplicates, keep only the first file of each group
    if (intraBatchDuplicates.length > 0) {
      const seenHashes = new Set<string>();
      nonDuplicates = nonDuplicates.filter((file) => {
        const isIntraDup = intraBatchDuplicates.some(
          (group) => group.hash === file.fileHash
        );
        if (isIntraDup) {
          if (seenHashes.has(file.fileHash)) {
            return false;
          }
          seenHashes.add(file.fileHash);
        }
        return true;
      });
    }

    const newQueuedDocs: (Document & { uploadId?: string })[] =
      nonDuplicates.map((file) => ({
        filename: file.name,
        fileHash: file.fileHash,
        status: DocumentStatus.DRAFT,
        createdAt: new Date().toISOString(),
        authorId: currentUser!.id,
        author: currentUser!,
        folderId: caseId,
        previewUrl: "",
        size: file.size,
        type: file.type,
        contentType: file.type,
        downloadCount: 0,
        id: `queued-${file.name}-${Date.now()}`,
        s3Key: "",
        updatedAt: new Date().toISOString(),
        url: "",
        batchKey: sessionBatchKey,
        isStructured: false,
        structuredS3Key: "",
        uploadSize: file.size,
      }));

    setQueuedDocuments((prev) => {
      const filteredPrev = prev.filter(
        (doc) => !nonDuplicates.some((file) => file.name === doc.filename)
      );
      return [...filteredPrev, ...newQueuedDocs];
    });

    await uploadDocuments(nonDuplicates as FileWithHash[]);
    setPendingFiles([]);
    setDuplicateFiles([]);
    setDuplicateMatches([]);
    setIntraBatchDuplicates([]);
  };

  const handleDuplicateCancel = () => {
    setShowDuplicatesModal(false);
    setPendingFiles([]);
    setDuplicateFiles([]);
    setDuplicateMatches([]);
    setIntraBatchDuplicates([]);
  };

  const counts: DocumentStatusCounts = useMemo(() => {
    return {
      fileCount: data?.documents?.length || 0,
      processing_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.PROCESSING
      ),
      error_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.ERROR
      ),
      complete_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.COMPLETE
      ),
      draft_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.DRAFT
      ),
      uploaded_count: findDocumentStatusCounts(
        data?.documents,
        DocumentStatus.UPLOADED
      ),
    };
  }, [data?.documents]);

  // Combine actual documents with queued documents for display
  const allDocuments = useMemo(() => {
    const apiDocs = data?.documents || [];
    const apiDocsMap = new Map(apiDocs.map((doc) => [doc.uploadId, doc]));
    const result: Document[] = [];

    queuedDocuments.forEach((queuedDoc) => {
      const apiDoc = apiDocsMap.get(queuedDoc.uploadId);
      if (apiDoc) {
        result.push(apiDoc);
        apiDocsMap.delete(queuedDoc.uploadId);
      } else {
        result.push({
          ...queuedDoc,
          id: `queued-${queuedDoc.filename}-${Date.now()}`,
          projectId: caseId,
          organizationId,
          authorId: currentUser?.id || "1",
          createdAt: queuedDoc.createdAt,
          updatedAt: queuedDoc.createdAt,
          status: DocumentStatus.DRAFT,
          s3Key: "",
          downloadCount: 0,
          batchKey: queuedDoc.batchKey,
        } as Document);
      }
    });

    apiDocsMap.forEach((doc) => result.push(doc));

    return result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [
    queuedDocuments,
    data?.documents,
    caseId,
    organizationId,
    currentUser?.id,
  ]);

  const totalPages = useMemo(() => {
    return allDocuments.reduce((acc, doc) => acc + (doc.pageCount || 0), 0);
  }, [allDocuments]);

  const completedPages = useMemo(() => {
    return allDocuments
      .filter((doc) => doc.status === DocumentStatus.COMPLETE)
      .reduce((acc, doc) => acc + (doc.pageCount || 0), 0);
  }, [allDocuments]);

  const allFilesNotProcessed = useMemo(() => {
    return allDocuments.some((doc) => doc.status !== DocumentStatus.COMPLETE);
  }, [allDocuments]);

  return {
    documents: allDocuments,
    isLoading,
    isError,
    uploadDocuments,
    isEmpty,
    hasOnlyArchivedDocuments,
    bulkUpdateDocuments,
    handleUpload,
    duplicateFiles,
    duplicateMatches,
    intraBatchDuplicates,
    pendingFiles,
    showDuplicatesModal,
    handleDuplicateConfirm,
    handleContinueWithoutDuplicates,
    handleDuplicateCancel,
    uploadProgress,
    pendingUploads,
    activeUploads,
    hasWaitingDocuments,
    counts,
    queuedDocuments,
    totalPages,
    completedPages,
    allFilesNotProcessed,
    cancelDraftUpload,
  };
};
